import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { PageHeader, ActionBar } from "@/components/PageBits";
import AIAnalyzeCard from "@/components/AIAnalyzeCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const empty = {
  auftragsnummer: "",
  kls_id: "",
  ausfuehrungsdatum: new Date().toISOString().slice(0, 10),
  auftragstyp: "FTTH",
  mitarbeiter_1: "",
  mitarbeiter_2: "",
  mitarbeiter_3: "",
  mitarbeiter_4: "",
  fahrzeug_id: "",
  telekom_konto: "",
  subunternehmer: false,
  bemerkung: "",
  preisliste_override: "",
};

const NONE = "__none__";
const fmt = (n) => (Number(n) || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function OrderForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const editMode = !!id;

  const [form, setForm] = useState(empty);
  const [users, setUsers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pricelists, setPricelists] = useState({});
  // mengen: { pos_nummer -> menge }
  const [mengen, setMengen] = useState({});
  const [conflictFlag, setConflictFlag] = useState(false);

  // load reference data
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/pricelists").then((r) => setPricelists(r.data)).catch(() => {});
  }, []);

  // load order if editing
  useEffect(() => {
    if (id) {
      api.get(`/orders/${id}`).then((r) => {
        const o = r.data;
        setForm({
          auftragsnummer: o.auftragsnummer || "",
          kls_id: o.kls_id || "",
          ausfuehrungsdatum: o.ausfuehrungsdatum || "",
          auftragstyp: o.auftragstyp || "FTTH",
          mitarbeiter_1: o.mitarbeiter_1 || "",
          mitarbeiter_2: o.mitarbeiter_2 || "",
          mitarbeiter_3: o.mitarbeiter_3 || "",
          mitarbeiter_4: o.mitarbeiter_4 || "",
          fahrzeug_id: o.fahrzeug_id || "",
          telekom_konto: o.telekom_konto || "",
          subunternehmer: !!o.subunternehmer,
          bemerkung: o.bemerkung || "",
          preisliste_override: o.preisliste_override || "",
        });
        const m = {};
        (o.aufmass || []).forEach((p) => {
          m[p.pos_nummer] = (m[p.pos_nummer] || 0) + Number(p.menge || 0);
        });
        setMengen(m);
        if (o.status === "Konflikt") setConflictFlag(true);
      });
    } else if (loc.state?.prefill) {
      setForm((f) => ({ ...f, ...loc.state.prefill }));
    }
  }, [id, loc.state]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const handle = (k) => (v) => set(k, v === NONE ? "" : v);

  // Compute current pricelist number
  const mitarbeiterCount = useMemo(
    () => ["mitarbeiter_1", "mitarbeiter_2", "mitarbeiter_3", "mitarbeiter_4"].filter((k) => form[k]).length,
    [form],
  );
  // Effektive Preisstufe: Subunternehmer → 5, Override (P1/P1.5) → gewählt,
  // sonst automatisch aus Mitarbeiter-Anzahl.
  const autoPreisliste = form.subunternehmer ? "5" : String(Math.max(1, Math.min(4, mitarbeiterCount || 1)));
  const canPickP15 = !form.subunternehmer && mitarbeiterCount <= 1;
  const preisliste = canPickP15 && (form.preisliste_override === "1" || form.preisliste_override === "1.5")
    ? form.preisliste_override
    : autoPreisliste;
  const activePL = pricelists[String(preisliste)] || {};
  const positionsList = useMemo(() => Object.entries(activePL).map(([code, m]) => ({ code, ...m })), [activePL]);

  // Auftragssumme
  const auftragssumme = useMemo(() => {
    let s = 0;
    for (const [code, m] of Object.entries(mengen)) {
      const price = activePL[code]?.price || 0;
      s += (Number(m) || 0) * price;
    }
    return Math.round(s * 100) / 100;
  }, [mengen, activePL]);

  const arbeiterAnzahl = form.subunternehmer ? 1 : Math.max(1, mitarbeiterCount);
  const personalkostenGesamt = Math.round(auftragssumme * arbeiterAnzahl * 100) / 100;

  // AI handlers
  const applyAuftragData = (d) => {
    setForm((f) => ({
      ...f,
      auftragsnummer: d.auftragsnummer || f.auftragsnummer,
      kls_id: d.kls_id || f.kls_id,
      ausfuehrungsdatum: d.ausfuehrungsdatum || f.ausfuehrungsdatum,
      telekom_konto: d.telekom_konto || f.telekom_konto,
    }));
  };

  const applyAufmass = (d) => {
    // Fill mengen for matching pos_nummer in active pricelist
    setMengen((prev) => {
      const next = { ...prev };
      let matched = 0;
      let unmatched = 0;
      (d.positionen || []).forEach((p) => {
        const code = String(p.pos_nummer || "").trim();
        const menge = Number(p.menge || 0);
        if (code && activePL[code]) {
          next[code] = (Number(next[code]) || 0) + menge;
          matched++;
        } else {
          unmatched++;
        }
      });
      if (unmatched > 0) {
        toast.warning(`${unmatched} Position(en) nicht in Preisliste ${preisliste} gefunden — Status auf Konflikt`);
        setConflictFlag(true);
      } else if (matched > 0) {
        toast.success(`${matched} Menge(n) übernommen`);
      }
      return next;
    });
  };

  const setMenge = (code, v) => {
    const num = v === "" ? "" : Number(v);
    setMengen((prev) => ({ ...prev, [code]: num }));
  };

  const save = async () => {
    if (!form.auftragsnummer || !form.kls_id) {
      toast.error("Auftragsnummer und KLS-ID sind Pflichtfelder");
      return;
    }
    setSaving(true);
    try {
      // Build positions array (only non-zero) mit Snapshot-Einzelpreis
      const aufmass = positionsList
        .filter((p) => (Number(mengen[p.code]) || 0) > 0)
        .map((p) => ({
          pos_nummer: p.code,
          beschreibung: p.name,
          menge: Number(mengen[p.code]) || 0,
          einheit: "Stk",
          einzelpreis: Number(p.price) || 0,
        }));

      // Preisliste-Override: nur "1.5" (bewusster manueller Downgrade) oder ""
      // "1" ist bereits Auto-Standard bei 1 Mitarbeiter — kein Override nötig.
      const override = canPickP15 && form.preisliste_override === "1.5" ? "1.5" : "";

      let orderId = id;
      if (editMode) {
        const payload = { ...form, preisliste_override: override, aufmass };
        if (conflictFlag) payload.status = "Konflikt";
        const { data } = await api.put(`/orders/${id}`, payload);
        orderId = data.id;
      } else {
        const createPayload = { ...form, preisliste_override: override };
        const { data } = await api.post("/orders", createPayload);
        orderId = data.id;
        if (aufmass.length > 0) {
          const payload = { aufmass };
          if (conflictFlag) payload.status = "Konflikt";
          await api.put(`/orders/${orderId}`, payload);
        } else if (conflictFlag) {
          await api.put(`/orders/${orderId}`, { status: "Konflikt" });
        }
      }
      toast.success(editMode ? "Auftrag aktualisiert" : "Auftrag angelegt");
      nav(`/orders/${orderId}`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="order-form">
      <PageHeader
        title={editMode ? "Auftrag bearbeiten" : "Neuer Auftrag"}
        subtitle="Pflichtfelder sind mit * markiert · Aufmaß wird direkt unten erfasst"
      />

      {/* KI-Karten oben */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
        <AIAnalyzeCard
          dataTestid="ai-auftragsnummer"
          mode="auftrag"
          title="KI-Analyse: Auftragsnummer"
          subtitle="Auftragsnummer, KLS-ID, Datum, Telekom Konto"
          onApply={applyAuftragData}
        />
        <AIAnalyzeCard
          dataTestid="ai-aufmass"
          mode="aufmass"
          title="KI-Analyse: Auftrags-Aufmaß"
          subtitle="Positionsnummern und Mengen — direkt im Aufmaß"
          onApply={applyAufmass}
        />
      </div>

      {/* Auftragsdaten */}
      <div className="bg-white border rounded-md p-6 grid grid-cols-1 md:grid-cols-2 gap-5 mb-5" style={{ borderColor: "#E2E8F0" }}>
        <div>
          <Label>Auftragsnummer *</Label>
          <Input data-testid="auftragsnummer-input" className="h-12 mt-1.5" value={form.auftragsnummer} onChange={(e) => set("auftragsnummer", e.target.value)} disabled={editMode} />
        </div>
        <div>
          <Label>KLS-ID *</Label>
          <Input data-testid="kls-id-input" className="h-12 mt-1.5" value={form.kls_id} onChange={(e) => set("kls_id", e.target.value)} />
        </div>
        <div>
          <Label>Ausführungsdatum *</Label>
          <Input data-testid="datum-input" type="date" className="h-12 mt-1.5" value={form.ausfuehrungsdatum} onChange={(e) => set("ausfuehrungsdatum", e.target.value)} />
        </div>
        <div>
          <Label>Auftragstyp *</Label>
          <Select value={form.auftragstyp} onValueChange={(v) => set("auftragstyp", v)}>
            <SelectTrigger className="h-12 mt-1.5" data-testid="typ-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="FTTH">FTTH</SelectItem>
              <SelectItem value="GWV">GWV</SelectItem>
              <SelectItem value="NE4">NE4</SelectItem>
              <SelectItem value="Mischauftrag">Mischauftrag</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {[1, 2, 3, 4].map((i) => {
          const current = form[`mitarbeiter_${i}`];
          const activeNames = new Set(users.filter((u) => u.is_active).map((u) => u.name));
          const options = users.filter((u) => u.is_active || u.name === current);
          return (
            <div key={i}>
              <Label>Mitarbeiter {i}</Label>
              <Select value={current || NONE} onValueChange={handle(`mitarbeiter_${i}`)}>
                <SelectTrigger className="h-12 mt-1.5" data-testid={`mitarbeiter-${i}-select`}><SelectValue placeholder="— wählen —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>— keiner —</SelectItem>
                  {options.map((u) => (
                    <SelectItem key={u.id} value={u.name}>
                      {u.name}{!activeNames.has(u.name) ? " (inaktiv)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        })}

        <div>
          <Label>Fahrzeug</Label>
          <Select value={form.fahrzeug_id || NONE} onValueChange={handle("fahrzeug_id")}>
            <SelectTrigger className="h-12 mt-1.5" data-testid="fahrzeug-select"><SelectValue placeholder="— wählen —" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>— keins —</SelectItem>
              {vehicles.filter((v) => v.is_active || v.id === form.fahrzeug_id).map((v) => (
                <SelectItem key={v.id} value={v.id}>{v.kennzeichen} — {v.fahrzeug}{!v.is_active ? " (inaktiv)" : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Telekom Konto</Label>
          <Input data-testid="telekom-input" className="h-12 mt-1.5" value={form.telekom_konto} onChange={(e) => set("telekom_konto", e.target.value)} />
        </div>

        <div className="md:col-span-2 flex items-center justify-between bg-slate-50 border rounded-md px-4 py-3" style={{ borderColor: "#E2E8F0" }}>
          <div>
            <div className="font-semibold">Subunternehmer</div>
            <div className="text-xs text-slate-500">Wenn aktiv, wird Preisliste 5 verwendet</div>
          </div>
          <Switch checked={form.subunternehmer} onCheckedChange={(v) => set("subunternehmer", v)} data-testid="subunternehmer-switch" />
        </div>

        <div className="md:col-span-2">
          <Label>Bemerkung</Label>
          <Textarea data-testid="bemerkung-textarea" className="mt-1.5 min-h-[80px]" value={form.bemerkung} onChange={(e) => set("bemerkung", e.target.value)} />
        </div>
      </div>

      {/* Aufmaß direkt im Auftrag */}
      <div className="bg-white border rounded-md mb-5" style={{ borderColor: "#E2E8F0" }}>
        <div className="px-5 py-4 border-b flex items-center justify-between flex-wrap gap-3" style={{ borderColor: "#E2E8F0" }}>
          <div>
            <h3 className="font-bold text-base">Aufmaß</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {form.subunternehmer
                ? "P5 (Subunternehmer)"
                : `P${preisliste} (${mitarbeiterCount || 1} Mitarbeiter${preisliste === "1.5" ? " · Qualitätsmangel/Nachbesserung" : ""})`}
              {" · "}Tragen Sie nur die <strong>Mengen</strong> ein
            </p>
          </div>
          <div className="flex items-center gap-3">
            {canPickP15 && (
              <div className="flex items-center gap-2 bg-slate-50 border rounded-md px-3 py-1.5" style={{ borderColor: "#E2E8F0" }}>
                <Label className="text-xs uppercase tracking-wider text-slate-500 m-0">Qualität</Label>
                <Select
                  value={form.preisliste_override === "1.5" ? "1.5" : "1"}
                  onValueChange={(v) => set("preisliste_override", v === "1.5" ? "1.5" : "")}
                >
                  <SelectTrigger className="h-9 w-40" data-testid="preisliste-quality-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1" data-testid="preisliste-p1">P1 · sauber</SelectItem>
                    <SelectItem value="1.5" data-testid="preisliste-p1_5">P1.5 · Nachbesserung</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div data-testid="aufmass-preisliste-badge" className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-md text-white" style={{ background: preisliste === "1.5" ? "#B45309" : "#0A2540" }}>
              P{preisliste}
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-3 font-semibold w-32">Pos.-Nr.</th>
                <th className="text-left px-4 py-3 font-semibold">Beschreibung</th>
                <th className="text-right px-4 py-3 font-semibold w-32">Menge</th>
                <th className="text-right px-4 py-3 font-semibold w-32">Einzelpreis</th>
                <th className="text-right px-4 py-3 font-semibold w-36">Gesamt</th>
              </tr>
            </thead>
            <tbody>
              {positionsList.length === 0 && (
                <tr><td colSpan={5} className="py-8 text-center text-slate-400">Preisliste lädt…</td></tr>
              )}
              {positionsList.map((p) => {
                const menge = mengen[p.code];
                const lineTotal = (Number(menge) || 0) * p.price;
                return (
                  <tr key={p.code} className="border-t" style={{ borderColor: "#E2E8F0" }} data-testid={`aufmass-row-${p.code}`}>
                    <td className="px-4 py-2 mono font-semibold">{p.code}</td>
                    <td className="px-4 py-2 text-slate-700">{p.name}</td>
                    <td className="px-4 py-2">
                      <Input
                        className="h-10 text-right"
                        type="number"
                        step="0.01"
                        min="0"
                        value={menge ?? ""}
                        onChange={(e) => setMenge(p.code, e.target.value)}
                        placeholder="0"
                        data-testid={`menge-${p.code}`}
                      />
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-500">€ {fmt(p.price)}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-semibold">€ {fmt(lineTotal)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 bg-slate-50" style={{ borderColor: "#0A2540" }}>
                <td colSpan={4} className="px-4 py-3 text-right font-bold uppercase tracking-wider text-xs text-slate-500">Auftragssumme</td>
                <td className="px-4 py-3 text-right tabular-nums text-lg font-bold" data-testid="auftragssumme">€ {fmt(auftragssumme)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Mitarbeitervergütung */}
      <div className="bg-white border rounded-md p-5 mb-5 grid grid-cols-1 md:grid-cols-4 gap-4" style={{ borderColor: "#E2E8F0" }} data-testid="vergutung-card">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Preisliste</div>
          <div className="text-xl font-bold mt-1">P{preisliste}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Mitarbeiter</div>
          <div className="text-xl font-bold mt-1" data-testid="mitarbeiter-anzahl">{arbeiterAnzahl}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Verdienst pro Mitarbeiter</div>
          <div className="text-xl font-bold mt-1 tabular-nums" data-testid="verdienst-pro">€ {fmt(auftragssumme)}</div>
        </div>
        <div className="md:border-l md:pl-4" style={{ borderColor: "#E2E8F0" }}>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Gesamte Personalkosten</div>
          <div className="text-xl font-bold mt-1 tabular-nums" style={{ color: "#0A2540" }} data-testid="personalkosten-gesamt">€ {fmt(personalkostenGesamt)}</div>
        </div>
      </div>

      {/* Konflikt-Banner */}
      {conflictFlag && (
        <div data-testid="conflict-banner" className="bg-amber-50 border border-amber-300 text-amber-900 rounded-md p-3 mb-5 text-sm flex items-center justify-between">
          <div>⚠ Konflikthinweis: KI hat nicht alle Daten eindeutig erkannt — Status wird beim Speichern auf <strong>Konflikt</strong> gesetzt.</div>
          <button onClick={() => setConflictFlag(false)} className="text-xs font-semibold underline" data-testid="conflict-dismiss">Aufheben</button>
        </div>
      )}

      <ActionBar onSave={save} saving={saving} onNext={() => nav("/orders")} onPrint={() => window.print()} />
    </div>
  );
}
